import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Package } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { FormError } from '../components/ui/FormError'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog'

// Servicios (Asegúrate de importar el servicio que creaste en el paso 1)
import { unitService } from '../services/unit.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
interface UnitItem {
    id: number
    name: string
    abbreviation: string
}

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
    name: z.string().min(1, 'El nombre es obligatorio'),
    abbreviation: z.string().min(1, 'La abreviatura es obligatoria'),
})

// Extrae el tipo de dato para Typescript a partir del esquema Zod
type FormValidate = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function Units() {
    // ─── Estados (Variables Reactivas) ───
    const [data, setData] = useState<UnitItem[]>([])
    const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [refreshCount, setRefreshCount] = useState(0) // Usado para forzar que la tabla se recargue

    // Control de Modales
    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

    // Paginación
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10

    // ─── Formulario (React Hook Form) ───
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormValidate>({
        mode: 'onChange',
        resolver: zodResolver(formSchema),
        defaultValues: { name: '', abbreviation: '' }
    })

    // ─── Efecto: Cargar Datos de la API ───
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const response = await unitService.getItems({
                    search: searchQuery,
                    page: currentPage
                })

                // Ajustar según cómo tu backend devuelva el JSON:
                const items = response.data?.data || response.data || []
                setData(items)

                // Guardar metadatos de paginación del backend (Laravel)
                setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
            } catch (error) {
                toast.error('Error al cargar los datos')
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [refreshCount, searchQuery, currentPage]) // Al cambiar estas dependencias, este bloque vuelve a ejecutarse

    // Los datos ya vienen filtrados y paginados del backend
    const paginatedData = data

    // ─── Funciones de Interfaz ───
    const openCreateModal = () => {
        setEditingId(null)
        reset({ name: '', abbreviation: '' }) // Limpia el formulario
        setShowModal(true)
    }

    const openEditModal = (item: UnitItem) => {
        setEditingId(item.id)
        reset({ name: item.name, abbreviation: item.abbreviation }) // Carga datos viejos
        setShowModal(true)
    }

    // ─── Funciones API (Guardar y Eliminar) ───
    const onSubmit = async (formData: FormValidate) => {
        try {
            if (editingId) {
                // Editar
                await unitService.updateItem(editingId, formData)
                toast.success('Registro actualizado exitosamente')
            } else {
                // Crear
                await unitService.createItem(formData)
                toast.success('Registro creado exitosamente')
            }
            setShowModal(false)
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al guardar el registro')
        }
    }

    const executeDelete = async () => {
        if (!deleteConfirmId) return
        try {
            await unitService.deleteItem(deleteConfirmId)
            toast.success('Registro eliminado')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al eliminar')
        } finally {
            setDeleteConfirmId(null)
        }
    }

    // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
    return (
        <div className="space-y-6 p-6">

            {/* 1. Header y Buscador */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Módulo de Unidades</h1>
                    <p className="mt-1 text-sm text-slate-600">Administra las unidades de medida.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button onClick={openCreateModal} className="bg-primary text-primary-foreground hover:bg-primary/90">
                        <Plus className="mr-2 h-4 w-4" /> Nuevo Registro
                    </Button>
                </div>
            </div>
<GlobalFilterBar
    show={['search']}
    values={{
        search: searchQuery,
    }}
    onChange={(changes) => {
        if (changes.search !== undefined) {
            setSearchQuery(changes.search)
            setCurrentPage(1)
        }
    }}
    customOptions={{
        searchPlaceholder: 'Buscar unidad...',
    }}
/>
            {/* 2. Tabla de Datos */}
            {isLoading ? (
                // Esqueleto: el número de 'columns' debe coincidir con tus columnas reales
                <TableSkeleton
                    headers={['Nombre', 'Abreviatura', 'Acciones']}
                    columns={['text', 'text', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                                <tr>
                                    {/* Encabezados de Tabla */}
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Abreviatura</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item) => (
                                        <tr
    key={item.id}
    className="transition-colors hover:bg-table-row-hover"
>
                                            {/* Celdas de Datos */}
                                            <td className="px-6 py-4 font-medium text-table-foreground">{item.name}</td>
                                            <td className="px-6 py-4 text-table-foreground">{item.abbreviation}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal(item)} title="Editar"
                                                        className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        {/* Estado de "No hay registros" */}
                                         <td colSpan={8} className="px-4 py-16 text-center">
                                            <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron registros</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 3. Paginación */}
                    {paginationMeta.total > 0 && (
                        <GlobalPaginator
                            totalItems={paginationMeta.total}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            from={paginationMeta.from}
                            to={paginationMeta.to}
                        />
                    )}
                </div>
            )}

            {/* 4. Modal Crear/Editar */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar Registro' : 'Nuevo Registro'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

                        {/* Input Nombre */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-card-foreground">Nombre <span className="text-red-500">*</span></label>
                            {/* Con {...register('campo')} conectas el input con React Hook Form */}
                            <Input {...register('name')} placeholder="Ingresa el nombre" className={errors.name ? 'border-red-500' : ''} />
                            {/* Y aquí muestras el mensaje de error de Zod si ocurre */}
                            {errors.name && <FormError message={errors.name.message} />}
                        </div>

                        {/* Input Abreviación */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-card-foreground">Abreviación <span className="text-red-500">*</span></label>
                            <Input {...register('abbreviation')} placeholder="Ingresa la abreviación" className={errors.abbreviation ? 'border-red-500' : ''} />
                            {errors.abbreviation && <FormError message={errors.abbreviation.message} />}
                        </div>

                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting}  className="bg-primary hover:bg-primary/90">
                                {/* Loader discreto que se muestra mientras se guarda */}
                                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* 5. Modal Global de Confirmación para Eliminar */}
            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="registro"
            />

        </div>
    )
}
